import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { ItemCard, PlaylistCard } from '../components/cards';
import { IconLink, IconSearch } from '../components/icons';
import { Avatar, EmptyState, ErrorState, Loading, SectionTitle, TextInput } from '../components/ui';
import { useAsync, useDebounced } from '../hooks/useAsync';
import { itemsWord } from '../lib/format';
import { t } from '../i18n';

export function SearchPage() {
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState(params.get('q') ?? '');
  const debounced = useDebounced(query, 320);
  const term = debounced.trim();

  useEffect(() => {
    if (term === (params.get('q') ?? '')) return;
    if (term) params.set('q', term);
    else params.delete('q');
    setParams(params, { replace: true });
  }, [term, params, setParams]);

  const state = useAsync(() => (term ? api.search(term) : Promise.resolve(null)), [term]);
  const results = state.data;

  const nothingFound =
    results !== null &&
    results.items.length === 0 &&
    results.playlists.length === 0 &&
    results.users.length === 0;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>{t('nav.search')}</h1>
          <p className="page-head__sub">
            {t('search.subtitle')}
          </p>
        </div>
      </div>

      <div className="toolbar">
        <div className="search-field" style={{ flex: 1, maxWidth: 520 }}>
          <span className="search-field__icon">
            <IconSearch size={18} />
          </span>
          <TextInput
            type="search"
            aria-label={t('search.aria')}
            placeholder={t('search.placeholder')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            autoFocus
          />
        </div>
      </div>

      {!term ? (
        <EmptyState
          icon={<IconSearch />}
          title={t('search.emptyTitle')}
          body={t('search.emptyBody')}
        />
      ) : state.error ? (
        <ErrorState error={state.error} onRetry={state.reload} />
      ) : state.loading || !results ? (
        <Loading rows={3} />
      ) : nothingFound ? (
        <EmptyState
          icon={<IconSearch />}
          title={t('library.nothingFound')}
          body={t('search.nothingBody', { term })}
          action={
            <Link className="btn btn--outline" to="/app/library?add=1">
              <IconLink size={17} /> {t('search.addThisLink')}
            </Link>
          }
        />
      ) : (
        <div className="stack">
          {results.items.length > 0 ? (
            <section aria-labelledby="rs-items">
              <SectionTitle aside={<span className="tiny muted">{itemsWord(results.items.length)}</span>}>
                <span id="rs-items">{t('search.yourLinks')}</span>
              </SectionTitle>
              <div className="grid">
                {results.items.map((item) => (
                  <ItemCard key={item.id} item={item} to={`/app/items/${item.id}`} />
                ))}
              </div>
            </section>
          ) : null}

          {results.playlists.length > 0 ? (
            <section aria-labelledby="rs-playlists">
              <SectionTitle
                aside={
                  <span className="tiny muted">{t('count.playlists', { n: results.playlists.length })}</span>
                }
              >
                <span id="rs-playlists">{t('nav.playlists')}</span>
              </SectionTitle>
              <div className="playlist-grid">
                {results.playlists.map((playlist) => (
                  <PlaylistCard key={playlist.id} playlist={playlist} to={`/p/${playlist.id}`} />
                ))}
              </div>
            </section>
          ) : null}

          {results.users.length > 0 ? (
            <section aria-labelledby="rs-users">
              <SectionTitle>
                <span id="rs-users">{t('search.people')}</span>
              </SectionTitle>
              <div className="panel stack-sm">
                {results.users.map((person) => (
                  <div className="user-row" key={person.id}>
                    <Avatar name={person.displayName} src={person.avatarUrl} />
                    <div className="user-row__main">
                      <Link to={`/u/${person.username}`} className="strong clamp-1">
                        {person.displayName}
                      </Link>
                      <p className="tiny muted clamp-1">@{person.username}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ) : null}
        </div>
      )}
    </div>
  );
}
